import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { getNextMinimumValidDateTime } from '@ppwr/poker'
import { DS } from '@/design-system'
import { SettingsCard } from '@/components/layouts/SettingsCard'
import { Button } from '@/components/ANewDesign/Button/Button'
import { ToggleRow } from '@/components/layouts/ToggleRow'
import { ScheduleRepeatSection } from '@/components/ScheduleRepeatSection'
import { FormTextInput } from './components/FormTextInput'
import { InfoRow } from './components/InfoRow'
import { SelectPicker } from './components/SelectPicker'
import { TurnClockPicker } from './components/TurnClockPicker'
import { BlindIntervalPicker } from './components/BlindIntervalPicker'
import { RebuyCountPicker } from './components/RebuyCountPicker'
import { ScheduleDateTimeFields } from './components/ScheduleDateTimeFields'
import { Tooltip } from './components/Tooltip'
import { translate } from '../../i18n'
import { useCreateGameStore } from '../../stores/create-game-store'
import { numberWithCommas } from '@/utils/numberWithCommas'

const BLIND_OPTIONS = ['200/100', '400/200', '500/250', '1,000/500']

export type GroupTabProps = {
  /** Set when creating a game from within a lounge — enables the repeat-days schedule section and,
   * since recurring lounge schedules have nowhere to store a rebuy cap, hides the rebuy toggle
   * whenever a repeat day is selected. */
  isLounge?: boolean
  repeatDays?: number[]
  onRepeatDaysChange?: (days: number[]) => void
}

export interface GroupTabHandle {
  /** Validates game name and starting chips, surfacing inline field errors. Returns whether the
   * form is valid so callers (the footer button in index.tsx) can bail out before submitting. */
  validate: () => boolean
}

export const GroupTab = forwardRef<GroupTabHandle, GroupTabProps>(function GroupTab(
  { isLounge = false, repeatDays = [], onRepeatDaysChange },
  ref
) {
  const [customizing, setCustomizing] = useState(false)
  const [repeatExpanded, setRepeatExpanded] = useState(false)
  const [nameError, setNameError] = useState<string | undefined>()
  const [chipsError, setChipsError] = useState<string | undefined>()
  const { group, setGroup, resetGroup } = useCreateGameStore()
  const isRecurring = isLounge && repeatDays.length > 0

  const validateChips = (bigBlind = group.bigBlind) => {
    const minChips = bigBlind * 2
    const chipsErr =
      group.startingChips >= minChips
        ? undefined
        : translate('createGameV2.group.chipsRequired', { min: numberWithCommas(minChips) })
    setChipsError(chipsErr)
    return chipsErr
  }

  useImperativeHandle(ref, () => ({
    validate: () => {
      // Leaving for now. Will probably revert this change immediately.
      // const nameErr = group.gameName.trim() ? undefined : translate('createGameV2.group.nameRequired')
      const nameErr = undefined
      const chipsErr = validateChips()
      setNameError(nameErr)
      if (chipsErr) setCustomizing(true)
      return !(nameErr || chipsErr)
    }
  }))

  const toggleRepeatDay = (day: number) => {
    if (!onRepeatDaysChange) return
    onRepeatDaysChange(
      repeatDays.includes(day) ? repeatDays.filter((d) => d !== day) : [...repeatDays, day].sort((a, b) => a - b)
    )
  }

  return (
    <SettingsCard gap={24} style={{ borderRadius: 16, borderWidth: 0, padding: 24 }}>
      <FormTextInput
        label={translate('createGameV2.group.gameName')}
        value={group.gameName}
        onChangeText={(text) => {
          setGroup({ gameName: text })
          if (nameError) setNameError(undefined)
        }}
        placeholder={translate('createGameV2.group.gameNamePlaceholder')}
        error={nameError}
      />

      {!customizing ? (
        <>
          <DS.Text variant="Caption/Bold" style={styles.appliedLabel}>
            {translate('createGameV2.group.appliedAutomatically')}
          </DS.Text>

          <DS.Frame gap="8">
            <InfoRow
              label={translate('createGameV2.group.startingChips')}
              value={numberWithCommas(group.startingChips)}
            />
            <InfoRow label={translate('createGameV2.group.blinds')} value={`${group.bigBlind / 2}/${group.bigBlind}`} />
            <InfoRow label={translate('createGameV2.group.turnClock')} value={`${group.turnDuration} sec`} />
            <InfoRow
              label={translate('createGameV2.group.blindIncreases')}
              value={
                group.blindIncreases
                  ? translate('createGameV2.minutesCount', { count: group.blindInterval })
                  : translate('createGameV2.none')
              }
            />
            <InfoRow
              label={translate('createGameV2.group.rebuy')}
              value={group.rebuy ? String(group.rebuyCount) : translate('createGameV2.none')}
            />
            <InfoRow
              label={translate('createGameV2.group.privateGame')}
              value={group.isPrivate ? translate('createGameV2.group.on') : translate('createGameV2.group.off')}
            />
          </DS.Frame>

          <View style={[styles.separator, { backgroundColor: DS.palette['Brand/Light Grey'] }]} />

          <Button
            type="outline"
            size="large"
            text={translate('createGameV2.group.customize')}
            onPress={() => setCustomizing(true)}
            containerStyle={[{ paddingVertical: 14, borderColor: DS.palette['Brand/Light Grey'] }]}
            textStyle={[{ fontWeight: '500' }]}
          />
        </>
      ) : (
        <DS.Frame gap="24">
          <DS.Frame gap="8">
            <DS.Frame row gap="20">
              <View style={styles.dropdownCol}>
                <FormTextInput
                  label={translate('createGameV2.group.startingChips')}
                  value={numberWithCommas(group.startingChips)}
                  onChangeText={(val) => {
                    const num = Number(val.replace(/\D/g, ''))
                    if (!Number.isNaN(num)) setGroup({ startingChips: num })
                    if (chipsError) setChipsError(undefined)
                  }}
                  keyboardType="numeric"
                  error={chipsError}
                  hideErrorText
                  onBlur={() => validateChips()}
                />
              </View>
              <View style={styles.dropdownCol}>
                <DS.Frame row alignItems="center" gap="4">
                  <DS.Text variant="Body Medium/Regular">{translate('createGameV2.group.startingBlind')}</DS.Text>
                  <Tooltip text={translate('createGameV2.group.tooltips.startingBlind')}>
                    <DS.Icon name="Info--filled" width={16} height={16} />
                  </Tooltip>
                </DS.Frame>
                <SelectPicker
                  data={BLIND_OPTIONS}
                  defaultValue={`${numberWithCommas(group.bigBlind)}/${numberWithCommas(Math.floor(group.bigBlind / 2))}`}
                  onSelect={(val) => {
                    const newBigBlind = Number(val.split('/')[0].replace(/,/g, ''))
                    setGroup({ bigBlind: newBigBlind })
                    validateChips(newBigBlind)
                  }}
                />
              </View>
            </DS.Frame>
            {chipsError ? (
              <DS.Text variant="Caption/Regular" style={{ color: DS.palette['Utility/Error'], fontSize: 16 }}>
                {chipsError}
              </DS.Text>
            ) : null}
          </DS.Frame>

          <DS.Frame gap="8">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.group.blindIncreasesInterval')}</DS.Text>
            <BlindIntervalPicker
              value={group.blindIncreases ? group.blindInterval : null}
              onChange={(minutes) =>
                setGroup({ blindIncreases: minutes !== null, blindInterval: minutes ?? group.blindInterval })
              }
            />
          </DS.Frame>

          <DS.Frame gap="8">
            <DS.Text variant="Body Medium/Regular">{translate('createGameV2.group.turnClock')}</DS.Text>
            <TurnClockPicker value={group.turnDuration} onChange={(val) => setGroup({ turnDuration: val })} />
          </DS.Frame>

          {!isRecurring && (
            <DS.Frame gap="8">
              <View>
                <DS.Text variant="Body Large/Regular">{translate('createGameV2.group.allowRebuy')}</DS.Text>
                <DS.Text variant="Caption/Regular" style={styles.rebuySubtitle}>
                  {translate('createGameV2.group.allowRebuySubtitle')}
                </DS.Text>
              </View>
              <RebuyCountPicker
                value={group.rebuy ? group.rebuyCount : null}
                onChange={(count) => setGroup({ rebuy: count !== null, rebuyCount: count ?? group.rebuyCount })}
              />
            </DS.Frame>
          )}

          {/* TODO: re-enable Advisor for Group games once supported
          <DS.Frame row justifyContent="space-between" alignItems="center">
            <DS.Frame row alignItems="center" gap="4">
              <DS.Text variant="Body Medium/Regular">{translate('createGameV2.group.advisor')}</DS.Text>
              <Tooltip text={translate('createGameV2.group.tooltips.advisor')}>
                <DS.Icon name="Info--filled" width={16} height={16} />
              </Tooltip>
            </DS.Frame>
            <ToggleRow label="" value={group.advisor} onValueChange={(val) => setGroup({ advisor: val })} />
          </DS.Frame>
          */}

          <View style={[styles.separator, { backgroundColor: DS.palette['Brand/Light Grey'] }]} />

          <DS.Frame gap="8">
            <ToggleRow
              label={translate('createGameV2.group.scheduleForLater')}
              value={group.scheduleForLater}
              onValueChange={(val) => {
                const needsFreshDate = val && group.scheduleDate.getTime() <= Date.now()
                setGroup({
                  scheduleForLater: val,
                  ...(needsFreshDate && { scheduleDate: getNextMinimumValidDateTime() })
                })
              }}
              style={styles.scheduleForLaterRow}
            />
            {group.scheduleForLater && (
              <ScheduleDateTimeFields value={group.scheduleDate} onChange={(val) => setGroup({ scheduleDate: val })} />
            )}
            {isLounge && group.scheduleForLater && (
              <ScheduleRepeatSection
                repeatDays={repeatDays}
                expanded={repeatExpanded}
                onToggleExpanded={() => setRepeatExpanded((expanded) => !expanded)}
                onToggleDay={toggleRepeatDay}
              />
            )}
            <View style={[styles.separator, { backgroundColor: DS.palette['Brand/Light Grey'] }]} />
          </DS.Frame>

          <Button
            type="outline"
            size="large"
            text={translate('createGameV2.group.reset')}
            onPress={() => {
              resetGroup()
              setNameError(undefined)
              setChipsError(undefined)
              setRepeatExpanded(false)
              onRepeatDaysChange?.([])
              setCustomizing(false)
            }}
            containerStyle={[{ paddingVertical: 14, borderColor: DS.palette['Brand/Light Grey'] }]}
            textStyle={[{ fontWeight: '500' }]}
          />
        </DS.Frame>
      )}
    </SettingsCard>
  )
})

const styles = StyleSheet.create({
  appliedLabel: {
    color: DS.palette['Brand/Dark Grey'],
    letterSpacing: 0.5,
    marginTop: 24
  },
  separator: {
    height: 1,
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  dropdownCol: {
    flex: 1
  },
  rebuySubtitle: {
    color: DS.palette['Brand/Dark Grey']
  },
  scheduleForLaterRow: {
    paddingVertical: 0,
    marginBottom: 24
  }
})
